<template>
  <div class="quarnx-swap-view">
    <h1 class="text-h4 mb-4">QuarnXSwap</h1>
    
    <!-- Alerta de Versão Beta -->
    <v-alert
      color="warning"
      icon="mdi-information-outline"
      variant="tonal"
      class="mb-6"
    >
      A QuarnXSwap está em fase beta. Esta versão é apenas uma demonstração visual das funcionalidades futuras.
    </v-alert>

    <!-- Componente de login / conexão de carteira se não estiver conectado -->
    <div v-if="!walletStore.isConnected">
      <WalletConnector @wallet-connected="handleWalletConnected" />
    </div>
    
    <!-- Interface principal após conexão de carteira -->
    <div v-else>
      <!-- Subabas para as diferentes funcionalidades da QuarnXSwap -->
      <v-tabs
        v-model="swapTab"
        fixed-tabs
        bg-color="surface-variant"
        slider-color="primary"
        class="mb-4 cross-chain-tabs"
      >

            <v-tab value="swap">
              <v-icon class="mr-2">mdi-swap-horizontal</v-icon>
              Swap
            </v-tab>
            <v-tab value="trading">
              <v-icon class="mr-2">mdi-chart-line</v-icon>
              Trading
            </v-tab>
            <v-tab value="bridge">
              <v-icon class="mr-2">mdi-bridge</v-icon>
              Bridge
            </v-tab>
            <v-tab value="pools">
              <v-icon class="mr-2">mdi-chart-bubble</v-icon>
              Pools
            </v-tab>
            <v-tab value="staking">
              <v-icon class="mr-2">mdi-safe-square</v-icon>
              Staking
            </v-tab>
            <v-tab value="portfolio">
              <v-icon class="mr-2">mdi-chart-pie</v-icon>
              Portfolio
            </v-tab>
            <v-tab value="history">
              <v-icon class="mr-2">mdi-history</v-icon>
              Histórico
            </v-tab>
          </v-tabs>
      
      <!-- Rede selecionada e ações da carteira -->
      <div class="d-flex justify-space-between align-center mb-6">
        <NetworkSelector v-model="selectedNetwork" />
        
        <div class="user-wallet-info">
          <v-menu
            v-model="walletMenu"
            :close-on-content-click="false"
            location="bottom"
          >
            <template v-slot:activator="{ props }">
              <v-chip
                color="primary"
                variant="outlined"
                prepend-icon="mdi-wallet-outline"
                size="large"
                class="wallet-chip"
                v-bind="props"
              >
                {{ formatAddress(walletStore.address) }}
                <v-icon end size="small" class="ml-2">mdi-chevron-down</v-icon>
              </v-chip>
            </template>
            
            <v-card min-width="320" max-width="420">
              <v-card-title class="d-flex align-center">
                <v-icon color="primary" class="mr-2">mdi-wallet</v-icon>
                Carteira {{ coin }}
              </v-card-title>
              
              <v-card-text>
                <div class="wallet-info mb-4">
                  <div class="d-flex justify-space-between align-center mb-2">
                    <span class="text-caption">Endereço:</span>
                    <v-chip variant="outlined" color="primary" size="small" class="copy-chip" @click="copyToClipboard(walletStore.address)">
                      <v-icon size="small" start>mdi-content-copy</v-icon>
                      Copiar
                    </v-chip>
                  </div>
                  <div class="wallet-address mb-4">{{ walletStore.address }}</div>
                  
                  <div class="d-flex justify-space-between align-center mb-1">
                    <span class="text-body-2">Saldo {{ coin }}:</span>
                    <span class="font-weight-medium">{{ walletStore.balanceFormatted }}</span>
                  </div>
                  
                  <div class="d-flex justify-space-between align-center">
                    <span class="text-body-2">Valor estimado:</span>
                    <span class="text-success">$3,126.25 USD</span>
                  </div>
                </div>
                
                <v-divider class="mb-4"></v-divider>
                
                <div class="wallet-actions mb-4">
                  <v-btn variant="text" prepend-icon="mdi-refresh" @click="atualizarSaldo" color="primary" class="mb-2" block>
                    Atualizar Saldo
                  </v-btn>
                  
                  <v-btn variant="text" prepend-icon="mdi-swap-horizontal" @click="mostrarEnviarDialogo" color="primary" class="mb-2" block>
                    Enviar {{ coin }}
                  </v-btn>
                  
                  <v-btn variant="text" prepend-icon="mdi-qrcode" @click="mostrarReceberDialogo" color="primary" block>
                    Receber
                  </v-btn>
                </div>
                
                <v-divider class="mb-4"></v-divider>
                
                <div class="switch-wallet mb-4">
                  <div class="text-subtitle-2 mb-2">Trocar de Carteira</div>
                  
                  <div class="wallet-list">
                    <div 
                      v-for="wallet in carteirasConectadas" 
                      :key="wallet.id"
                      class="wallet-item d-flex align-center pa-2"
                      :class="{'active-wallet': wallet.address === walletStore.address}"
                      @click="trocarCarteira(wallet)"
                    >
                      <v-avatar size="32" :color="wallet.network.color" class="mr-2">
                        <span class="text-caption text-white">{{ wallet.network.icon }}</span>
                      </v-avatar>
                      <div class="flex-grow-1">
                        <div class="d-flex align-center">
                          <span class="text-body-2 font-weight-medium">{{ wallet.name }}</span>
                          <v-chip v-if="wallet.isPrimary" size="x-small" class="ml-2" color="primary" text="Principal"></v-chip>
                        </div>
                        <div class="text-caption">{{ formatAddress(wallet.address) }}</div>
                      </div>
                      <v-icon v-if="wallet.address === walletStore.address" color="success" size="small">mdi-check-circle</v-icon>
                    </div>
                  </div>
                  
                  <v-btn color="primary" variant="tonal" class="mt-4" prepend-icon="mdi-plus" block @click="mostrarConectarNovaCarteira">
                    Conectar Nova Carteira
                  </v-btn>
                </div>
                
                <v-btn color="error" variant="tonal" prepend-icon="mdi-logout" block @click="disconnectWallet">
                  Desconectar
                </v-btn>
              </v-card-text>
            </v-card>
          </v-menu>
        </div>
      </div>
      
      <!-- Conteúdo das subabas da QuarnXSwap -->
      <v-window v-model="swapTab">
        <!-- Swap Tab -->
        <v-window-item value="swap">
          <SwapInterface />
        </v-window-item>
        
        <!-- Trading Tab -->
        <v-window-item value="trading">
          <TradingInterface />
        </v-window-item>
        
        <!-- Bridge Tab -->
        <v-window-item value="bridge">
          <BridgeInterface />
        </v-window-item>
        
        <!-- Pools Tab -->
        <v-window-item value="pools">
          <PoolsInterface />
        </v-window-item>
        
        <!-- Staking Tab -->
        <v-window-item value="staking">
          <LpStakingInterface />
        </v-window-item>
        
        <!-- Portfolio Tab -->
        <v-window-item value="portfolio">
          <PortfolioInterface />
        </v-window-item>
        
        <!-- History Tab -->
        <v-window-item value="history">
          <TransactionHistory />
        </v-window-item>
      </v-window>
    </div>
    
    <!-- Diálogos para enviar e receber KinX -->
    <SendKinXDialog v-model="enviarDialogoAberto" />
    <ReceiveKinXDialog v-model="receberDialogoAberto" />
    
    <!-- Diálogo de staking -->
    <LpStakingDialog 
      v-model:show="stakingDialogOpen" 
      :stakingPool="selectedStakingPool"
      :userStake="selectedUserStake"
      :mode="stakingDialogMode"
      @stake-confirmed="handleStakeConfirmed"
      @unstake-confirmed="handleUnstakeConfirmed"
      @claim-rewards="handleClaimRewards"
      @change-mode="changeStakingMode"
    />
  </div>
</template>

<script>
import { useDexStore } from '@/stores/dex'
import { useWalletStore } from '@/stores/wallet'
import { useAppStore } from '@/stores/app'
import WalletConnector from '@/components/dex/WalletConnector.vue'
import TradingInterface from '@/components/dex/TradingInterface.vue'
import SwapInterface from '@/components/dex/SwapInterface.vue'
import BridgeInterface from '@/components/dex/BridgeInterface.vue'
import PoolsInterface from '@/components/dex/PoolsInterface.vue'
import LpStakingInterface from '@/components/dex/LpStakingInterface.vue'
import LpStakingDialog from '@/components/dex/LpStakingDialog.vue'
import PortfolioInterface from '@/components/dex/PortfolioInterface.vue'
import TransactionHistory from '@/components/dex/TransactionHistory.vue'
import NetworkSelector from '@/components/dex/NetworkSelector.vue'
import DexContent from '@/components/dex/DexContent.vue'
import SendKinXDialog from '@/components/dex/SendKinXDialog.vue'
import ReceiveKinXDialog from '@/components/dex/ReceiveKinXDialog.vue'

export default {
  name: 'QuarnXSwapView',
  
  components: {
    WalletConnector,
    TradingInterface,
    SwapInterface,
    BridgeInterface,
    PoolsInterface,
    LpStakingInterface,
    LpStakingDialog,
    PortfolioInterface,
    TransactionHistory,
    NetworkSelector,
    DexContent,
    SendKinXDialog,
    ReceiveKinXDialog
  },
  
  provide() {
    return {
      openStakingDialog: this.openStakingDialog
    }
  },
  
  data() {
    return {
      mainTab: 'dex',
      swapTab: 'swap',
      walletMenu: false,
      selectedNetwork: {
        id: 'quarnx',
        name: 'QuarnX',
        color: 'primary',
        icon: 'Q'
      },
      carteirasConectadas: [
        {
          id: 1,
          name: 'QuarnX Principal',
          address: 'Q2SDbLv2omPHSGrMX3tJBwYvXbJF5g7LLnH',
          isPrimary: true,
          network: {
            id: 'quarnx',
            name: 'QuarnX',
            color: 'primary',
            icon: 'Q'
          }
        },
        {
          id: 2,
          name: 'Ethereum',
          address: '0x71C7656EC7ab88b098defB751B7401B5f6d8976F',
          isPrimary: false,
          network: {
            id: 'ethereum',
            name: 'Ethereum',
            color: '#6B7FFF',
            icon: 'E'
          }
        },
        {
          id: 3,
          name: 'BSC',
          address: '0x2Fa9c0a328a710a65Ed8Ff02c77b021C30338e68',
          isPrimary: false,
          network: {
            id: 'bsc',
            name: 'BSC',
            color: '#F0B90B',
            icon: 'B'
          }
        }
      ],
      enviarDialogoAberto: false,
      receberDialogoAberto: false,
      stakingDialogOpen: false,
      stakingDialogMode: 'info',
      selectedStakingPool: null,
      selectedUserStake: null,
    }
  },
  
  computed: {
    dexStore() {
      return useDexStore()
    },
    walletStore() {
      return useWalletStore()
    },
    appStore() {
      return useAppStore()
    }
  },
  
  methods: {
    async handleWalletConnected(address) {
      // Use walletStore for wallet management
      if (!this.walletStore.isConnected) {
        await this.walletStore.createWallet()
      }
      this.mainTab = 'quarnxswap'
    },
    
    disconnectWallet() {
      this.walletStore.disconnect()
      this.walletMenu = false
      this.mainTab = 'dex'
    },
    
    copyToClipboard(text) {
      // Função para copiar para a área de transferência
      navigator.clipboard.writeText(text).then(() => {
        // Mostra notificação de sucesso
        this.appStore.showSnackbar({
          text: 'Endereço copiado para a área de transferência',
          color: 'success'
        })
      })
      
      this.walletMenu = false
    },
    
    atualizarSaldo() {
      this.walletStore.fetchBalance()
      this.walletMenu = false
    },
    
    mostrarEnviarDialogo() {
      this.enviarDialogoAberto = true
      this.walletMenu = false
    },
    
    mostrarReceberDialogo() {
      this.receberDialogoAberto = true
      this.walletMenu = false
    },
    
    mostrarConectarNovaCarteira() {
      // Implementação futura
      this.walletMenu = false
    },
    
    trocarCarteira(wallet) {
      // For multi-wallet support, switch the active wallet
      // Currently walletStore manages a single wallet
      this.selectedNetwork = wallet.network
      
      this.appStore.showSnackbar({
        text: `Carteira alterada para ${wallet.name}`,
        color: 'info'
      })
      
      this.walletMenu = false
    },
    
    formatAddress(address) {
      if (!address) return ''
      return address.substring(0, 6) + '...' + address.substring(address.length - 4)
    },
    
    openStakingDialog(stakingPool, userStake = null, mode = 'info') {
      this.selectedStakingPool = stakingPool
      this.selectedUserStake = userStake
      this.stakingDialogMode = mode
      this.stakingDialogOpen = true
    },
    
    changeStakingMode(mode) {
      this.stakingDialogMode = mode
    },
    
    handleStakeConfirmed(stakeData) {
      this.appStore.showSnackbar({
        text: `Stake de LP tokens na pool ${this.selectedStakingPool.pool.token1.symbol}/${this.selectedStakingPool.pool.token2.symbol} confirmado`,
        color: 'success'
      })
      // Aqui implementaríamos a lógica real de stake com o backend
    },
    
    handleUnstakeConfirmed(unstakeData) {
      this.appStore.showSnackbar({
        text: `Unstake de LP tokens confirmado`,
        color: 'success'
      })
      // Aqui implementaríamos a lógica real de unstake com o backend
    },
    
    handleClaimRewards(claimData) {
      this.appStore.showSnackbar({
        text: `Recompensas de ${claimData.amount} ${window.__QUARNX_COIN__ || 'tKinX'} reclamadas com sucesso`,
        color: 'success'
      })
      // Aqui implementaríamos a lógica real de claim com o backend
    },
  },
  
  async mounted() {
    // Restore wallet from storage if available
    await this.walletStore.restoreFromStorage()
  }
}
</script>

<style scoped>
.quarnx-swap-view {
  max-width: 1400px;
  margin: 0 auto;
}

.cross-chain-tabs {
  border-radius: 8px;
  overflow: hidden;
}

.wallet-chip {
  cursor: pointer;
  font-weight: 500;
  padding-right: 8px;
}

.wallet-address {
  background-color: rgba(0, 0, 0, 0.03);
  border-radius: 4px;
  padding: 8px;
  font-family: monospace;
  word-break: break-all;
  font-size: 0.85rem;
}

.copy-chip {
  cursor: pointer;
}

.wallet-list {
  max-height: 200px;
  overflow-y: auto;
}

.wallet-item {
  border-radius: 4px;
  cursor: pointer;
  transition: background-color 0.2s;
}

.wallet-item:hover {
  background-color: rgba(0, 0, 0, 0.03);
}

.active-wallet {
  background-color: rgba(var(--v-theme-primary), 0.1);
}
</style>
